package com.solevo.dist.pages

import androidx.compose.runtime.*
import com.solevo.dist.components.CenteredLoader
import com.solevo.dist.components.DashboardPageLayout
import com.solevo.dist.components.RequireAuth
import com.solevo.dist.components.WaitForAuthAndViewModel
import com.solevo.dist.viewmodels.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.core.rememberPageContext
import com.solevo.dist.navigation.Screen
import com.solevo.dist.i18n.LocalStrings
import com.solevo.dist.components.StatusPill
import com.solevo.dist.theme.styles.tableHeaderCell
import com.solevo.dist.theme.styles.tableBodyCell
import com.solevo.dist.theme.styles.tableBodyCellBold
import com.solevo.dist.theme.styles.tableRow
import com.solevo.dist.theme.styles.tableHeaderRow
import com.solevo.dist.theme.styles.sectionTitle

@Page("/orders")
@Composable
fun OrdersScreen() {
    RequireAuth { OrdersPage() }
}

@Composable
fun OrdersPage() {
    WaitForAuthAndViewModel(
        key = "orders",
        viewModelFactory = { OrdersViewModel() },
        onViewModelReady = { vm -> vm.start() }
    ) { vm ->
        DisposableEffect(Unit) { onDispose { vm.stop() } }
        val ui by vm.state.collectAsState()

        DashboardPageLayout(header = {
            OrdersHeader(
                ui = ui,
                onTabSelected = { t -> vm.selectTab(t) }
            )
        }) {
            OrdersBody(ui, onSortToggle = { vm.toggleSortOrder() })
        }
    }
}

@Composable
private fun VSpace(h: CSSLengthValue) = Box(Modifier.height(h))

@Composable
private fun OrdersHeader(ui: OrdersUiState, onTabSelected: (OrdersTab) -> Unit) {
    Column(Modifier.fillMaxWidth().padding(24.px)) {
        val strings = LocalStrings.current
        SpanText(strings.ordersTitle, Modifier.sectionTitle())
        VSpace(20.px)

        Box(
            Modifier
                .fillMaxWidth()
                .position(Position.Relative)
        ) {
            Box(
                Modifier
                    .position(Position.Absolute)
                    .left(0.px)
                    .right(0.px)
                    .bottom(1.px)
                    .height(1.px)
                    .backgroundColor(rgb(230, 232, 235))
                    .zIndex(0)
            )

            Row(
                Modifier
                    .gap(24.px)
                    .align(Alignment.BottomStart)
                    .zIndex(1)
            ) {
                OrdersTab.values().forEach { t ->
                    OrdersTopTab(
                        label = when (t) {
                            OrdersTab.All -> strings.ordersTabAll
                            OrdersTab.Created -> strings.ordersTabCreated
                            OrdersTab.Confirmed -> strings.ordersTabConfirmed
                            OrdersTab.Expired -> strings.ordersTabExpired
                            OrdersTab.Closed -> strings.ordersTabClosed
                            OrdersTab.Cancelled -> strings.ordersTabCancelled
                            OrdersTab.Error -> strings.ordersTabError
                        },
                        selected = ui.selectedTab == t,
                        onClick = { onTabSelected(t) }
                    )
                }
            }
        }
    }
}

@Composable
private fun OrdersBody(ui: OrdersUiState, onSortToggle: () -> Unit) {
    Box(Modifier.fillMaxWidth().padding(24.px)) {
        when {
            ui.loading -> CenteredLoader()
            ui.error != null -> ErrorBox(ui.error!!)
            else -> OrdersTable(ui.rows, ui.sortOrder, onSortToggle)
        }
    }
}

@Composable
private fun ErrorBox(msg: String) {
    Row(
        Modifier.fillMaxWidth()
            .backgroundColor(rgb(255, 246, 246))
            .border(1.px, LineStyle.Solid, rgb(255, 205, 205))
            .padding(12.px)
            .borderRadius(12.px)
    ) {
        val strings = LocalStrings.current
        SpanText("${strings.errorPrefix}: $msg")
    }
}

@Composable
private fun OrdersTable(orders: List<OrderRowUi>, sortOrder: com.solevo.dist.viewmodels.SortOrder, onSortToggle: () -> Unit) {
    val context = rememberPageContext()
    val strings = LocalStrings.current
    Column(
        Modifier.fillMaxWidth()
    ) {
        GridRow(
            modifier = Modifier.tableHeaderRow(),
            amount = strings.ordersHeaderAmount.uppercase(),
            status = " ",
            description = strings.ordersHeaderDescription.uppercase(),
            pickup = strings.ordersHeaderPickupDate.uppercase(),
            created = strings.ordersHeaderDateCreated.uppercase(),
            isHeader = true,
            sortOrder = sortOrder,
            onSortClick = onSortToggle
        )

        if (orders.isEmpty()) {
            com.solevo.dist.components.EmptyState(title = strings.ordersEmpty)
            return@Column
        }

        orders.forEachIndexed { i, o ->
            GridRow(
                modifier = Modifier,
                amount = o.amountText,
                status = localizeOrderStatus(LocalStrings.current, o.statusText),
                description = localizeOrderDescription(LocalStrings.current, o.description),
                pickup = o.pickupDateText,
                created = o.createdDateText,
                pillHex = o.statusColorHex,
                isHeader = false,
                onClick = { context.router.navigateTo(Screen.OrderDetailsPage.withId(o.id)) }
            )
        }
    }
}

@Composable
private fun GridRow(
    modifier: Modifier = Modifier,
    amount: String,
    status: String,
    description: String,
    pickup: String,
    created: String,
    pillHex: String? = null,
    isHeader: Boolean,
    onClick: (() -> Unit)? = null,
    sortOrder: com.solevo.dist.viewmodels.SortOrder? = null,
    onSortClick: (() -> Unit)? = null
) {
    Row(
        modifier
            .fillMaxWidth()
            .display(DisplayStyle.Grid)
            .styleModifier { property("grid-template-columns", "0.5fr 0.5fr 1fr 1fr 1fr") }
            .then(if (isHeader) Modifier.tableHeaderRow() else Modifier.tableRow())
            .then(if (!isHeader) Modifier.cursor(Cursor.Pointer) else Modifier)
            .onClick { if (!isHeader) onClick?.invoke() }
    ) {
        if (isHeader) {
            SpanText(amount, Modifier.tableHeaderCell())
        } else {
            SpanText(amount, Modifier.tableBodyCellBold())
        }
        if (isHeader) SpanText(status, Modifier.tableHeaderCell()) else Row { StatusPill(status, pillHex ?: "#888888", useIconForInvoiceStatuses = false) }
        SpanText(description, if (isHeader) Modifier.tableHeaderCell() else Modifier.tableBodyCell())
        SpanText(pickup, if (isHeader) Modifier.tableHeaderCell() else Modifier.tableBodyCell())
        if (isHeader) {
            Row(
                modifier = Modifier
                    .tableHeaderCell()
                    .then(if (onSortClick != null) Modifier.cursor(Cursor.Pointer) else Modifier)
                    .then(if (onSortClick != null) Modifier.onClick { onSortClick() } else Modifier)
                    .gap(4.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(created)
                if (sortOrder != null) {
                    val icon = when (sortOrder) {
                        com.solevo.dist.viewmodels.SortOrder.Ascending -> "↑"
                        com.solevo.dist.viewmodels.SortOrder.Descending -> "↓"
                    }
                    SpanText(icon, Modifier.fontSize(12.px))
                }
            }
        } else {
            SpanText(created, Modifier.tableBodyCell())
        }
    }
}



private fun localizeOrderStatus(s: com.solevo.dist.i18n.Strings, status: String): String {
    return when (status.lowercase()) {
        "initialized" -> s.orderStatusInitialized
        "created" -> s.orderStatusCreated
        "confirmed" -> s.orderStatusConfirmed
        "expired" -> s.orderStatusExpired
        "cancelled" -> s.orderStatusCancelled
        "closed" -> s.orderStatusClosed
        "error" -> s.orderStatusError
        else -> status
    }
}

private fun localizeOrderDescription(s: com.solevo.dist.i18n.Strings, desc: String): String {
    return if (desc.startsWith("Order ")) s.wordOrder + " " + desc.removePrefix("Order ") else desc
}


@Composable
private fun OrdersTopTab(
    label: String,
    selected: Boolean,
    onClick: () -> Unit
) {
    Box(
        Modifier
            .cursor(Cursor.Pointer)
            .padding(leftRight = 12.px, topBottom = 10.px)
            .styleModifier {
                if (selected) {
                    property("box-shadow", "inset 0 -3px 0 0 black")
                }
            }
            .onClick { onClick() }
    ) {
        SpanText(
            label,
            Modifier
                .fontSize(15.px)
                .fontWeight(if (selected) FontWeight.Bold else FontWeight.Normal)
                .color(rgb(36, 41, 47))
        )
    }
}

